import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaArrowLeft, FaPlus } from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const WorkLocationForm = () => {
  const BASE_URL = "https://nissi-india.com";

  const params = useParams();
  const editId = params?.id ? Number(params.id) : 0;
  const isEdit = Boolean(editId);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ edit data comes from list page
  const editData = location.state?.editData || null;

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ helper
  const toInt = (v) => {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : 0;
  };

  // ✅ parsed values
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  const [branchList, setBranchList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [errors, setErrors] = useState({});

  const addOthers = (arr) => {
    return [...arr, { id: -1, name: "Others" }];
  };

  const [formData, setFormData] = useState({
    wlid: 0,
    workLocationName: "",
    code: "",

    branch: "",
    address1: "",
    address2: "",

    country: "",
    state: "",
    district: "",
    city: "",

    otherCountryName: "",
    otherStateName: "",
    otherDistrictName: "",
    otherCityName: "",

    zipcode: "",
    contactPerson: "",
    email: "",
    phoneNumber: "",
    mobile: "",
    remarks: "",
  });

  const isOthersSelected = (val) => String(val) === "-1";

  const fetchNextCode = async () => {
    try {
      if (!userId) return;

      const res = await axios.get(`${BASE_URL}/api/workLocation/nextCode`, {
        params: { loginuserid: userId },
        headers: commonHeaders,
      });

      if (res.data.success) {
        setFormData((prev) => ({ ...prev, code: res.data.code }));
      } else {
        toast.error(res.data.message || "Failed to generate code");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error generating work location code");
    }
  };

  const fetchBranchesAndCountries = async () => {
    try {
      const [bRes, cRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/workLocation/branches`, {
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/workLocation/countries`, {
          headers: commonHeaders,
        }),
      ]);

      if (bRes.data.success) setBranchList(addOthers(bRes.data.list || []));
      if (cRes.data.success) setCountryList(addOthers(cRes.data.list || []));
    } catch (err) {
      console.error(err);
      toast.error("Error loading branch/country");
    }
  };

  const fetchStates = async (countryid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/workLocation/states`, {
        params: { countryid },
        headers: commonHeaders,
      });

      if (res.data.success) setStateList(addOthers(res.data.list || []));
    } catch (err) {
      console.error(err);
      toast.error("Error loading states");
    }
  };

  const fetchDistricts = async (stateid) => {
    try {
      const dRes = await axios.get(`${BASE_URL}/api/workLocation/districts`, {
        params: { stateid },
        headers: commonHeaders,
      });

      if (dRes.data.success) setDistrictList(addOthers(dRes.data.list || []));
    } catch (err) {
      console.error(err);
      toast.error("Error loading districts");
    }
  };

  const fetchCitiesByDistrict = async (districtid) => {
    try {
      const ctRes = await axios.get(`${BASE_URL}/api/workLocation/cities`, {
        params: { districtid },
        headers: commonHeaders,
      });

      if (ctRes.data.success) setCityList(addOthers(ctRes.data.list || []));
    } catch (err) {
      console.error(err);
      toast.error("Error loading cities");
    }
  };

  const fetchById = async (wlid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/workLocation/getById`, {
        params: { wlid },
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Failed to load details");
        return;
      }

      const d = res.data.data;

      setFormData((prev) => ({
        ...prev,
        wlid: d.wlid,
        branch: String(d.branchid || ""),
        workLocationName: d.workLocationName || "",
        code: d.code || "",
        contactPerson: d.contactPerson || "",
        address1: d.address1 || "",
        address2: d.address2 || "",
        country: String(d.countryid || ""),
        state: String(d.stateid || ""),
        district: String(d.districtid || ""),
        city: String(d.cityid || ""),
        zipcode: d.zipcode || "",
        phoneNumber: d.phoneNumber || "",
        mobile: d.mobile || "",
        email: d.email || "",
        remarks: d.remarks || "",
      }));

      if (d.countryid) await fetchStates(d.countryid);
      if (d.stateid) await fetchDistricts(d.stateid);
      if (d.districtid) await fetchCitiesByDistrict(d.districtid);
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading details");
    }
  };

  useEffect(() => {
    fetchBranchesAndCountries();

    if (branchType !== "HO" && branchId) {
      setFormData((prev) => ({ ...prev, branch: String(branchId) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit && editData) {
      setFormData((prev) => ({
        ...prev,
        wlid: editData.wlid || 0,
        workLocationName: editData.wlname || "",
        code: editData.wlcode || prev.code,
        address1: editData.addr1 || "",
        address2: editData.addr2 || "",
      }));
    }
  }, [isEdit, editData]);

  useEffect(() => {
    if (isEdit) {
      fetchById(editId);
    } else {
      fetchNextCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId]);

  const handleCountryChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      country: val,
      state: "",
      district: "",
      city: "",
      otherCountryName: "",
      otherStateName: "",
      otherDistrictName: "",
      otherCityName: "",
    }));

    setStateList([]);
    setDistrictList([]);
    setCityList([]);

    if (val && !isOthersSelected(val)) fetchStates(val);
  };

  const handleStateChange = (val) => {
    const v = String(val);

    if (v === "-1") {
      setFormData((prev) => ({
        ...prev,
        state: v,
        district: "-1",
        city: "-1",
        otherStateName: "",
        otherDistrictName: "",
        otherCityName: "",
      }));

      setDistrictList([]);
      setCityList([]);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      state: v,
      district: "",
      city: "",
      otherStateName: "",
      otherDistrictName: "",
      otherCityName: "",
    }));

       setDistrictList([]);
    setCityList([]);

    if (v) fetchDistricts(v);
  };

  const handleDistrictChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      district: val,
      otherDistrictName: "",
      city: "",
      otherCityName: "",
    }));

    setCityList([]);

    if (val && !isOthersSelected(val)) {
      fetchCitiesByDistrict(val);
    }
  };

  const handleCityChange = (val) => {
    setFormData((prev) => ({ ...prev, city: val, otherCityName: "" }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const e = {};

    if (!formData.workLocationName.trim()) e.workLocationName = "Work Location Name is required";
    if (!formData.address1.trim()) e.address1 = "Address 1 is required";

    if (!formData.country) e.country = "Country is required";
    if (!formData.state) e.state = "State is required";
    if (!formData.district) e.district = "District is required";
    if (!formData.city) e.city = "City is required";

    if (!formData.contactPerson.trim()) e.contactPerson = "Contact Person is required";
    if (!formData.phoneNumber.trim()) e.phoneNumber = "Phone Number is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    const finalBranchId = branchType !== "HO" ? branchId : Number(formData.branch || 0);

    if (!finalBranchId) {
      toast.error("Branch is required");
      return;
    }

    if (!userId) {
      toast.error("Login UserId missing");
      return;
    }

    try {
      const payload = {
        wlid: isEdit ? formData.wlid : undefined,

        branchid: finalBranchId,
        workLocationName: formData.workLocationName,
        code: formData.code,
        contactPerson: formData.contactPerson,
        address1: formData.address1,
        address2: formData.address2,
        zipcode: formData.zipcode,
        phoneNumber: formData.phoneNumber,
        mobile: formData.mobile,
        email: formData.email,
        remarks: formData.remarks,
        loginuserid: userId,

        countryid: Number(formData.country || 0),
        stateid: Number(formData.state || 0),
        districtid: Number(formData.district || 0),
        cityid: Number(formData.city || 0),

        otherCountryName: formData.otherCountryName || "",
        otherStateName: formData.otherStateName || "",
        otherDistrictName: formData.otherDistrictName || "",
        otherCityName: formData.otherCityName || "",
      };

      const url = isEdit
        ? `${BASE_URL}/api/workLocation/update`
        : `${BASE_URL}/api/workLocation/save`;

      const res = await axios.post(url, payload, {
        headers: commonHeaders,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Operation failed");
        return;
      }

      if (res.data.warnings?.length) {
        res.data.warnings.forEach((w) => toast.warn(w));
      }

      toast.success(res.data.message || (isEdit ? "Updated successfully" : "Inserted successfully"));
      setTimeout(() => navigate(-1), 800);
    } catch (err) {
      console.error("SAVE/UPDATE ERROR:", err?.response?.data || err);
      toast.error(err?.response?.data?.message || "Server error while saving");
    }
  };

  const handleAddNew = () => {
    setFormData({
      wlid: 0,
      workLocationName: "",
      code: "",
      branch: branchType !== "HO" ? String(branchId) : "",
      address1: "",
      address2: "",
      country: "",
      state: "",
      district: "",
      city: "",
      otherCountryName: "",
      otherStateName: "",
      otherDistrictName: "",
      otherCityName: "",
      zipcode: "",
      contactPerson: "",
      email: "",
      phoneNumber: "",
      mobile: "",
      remarks: "",
    });
    setStateList([]);
    setDistrictList([]);
    setCityList([]);
    setErrors({});
    if (!isEdit) fetchNextCode();
    toast.info("Cleared for new entry");
  };

  const Label = ({ children, required }) => (
    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
      {children} {required ? <span className="text-red-500">*</span> : null}
    </label>
  );

  const inputBase = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const selectBase = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const textAreaBase = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK LOCATION MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
       <button
                onClick={() => navigate("/OrderManagement/WorkLocationMaster")}
               className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
              >
                <FaArrowLeft />
                Back
              </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? "Edit Work Location" : "Add Work Location"}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`px-3 py-1 rounded text-sm font-medium ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700"
                }`}
              >
                INFRA SCREEN : E-3
              </div>
            </div>
          </div>

          <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
              <div className="max-w-6xl mx-auto space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>Work Location Name</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.workLocationName}
                        onChange={(e) => handleInputChange("workLocationName", e.target.value)}
                        className={`${inputBase} ${errors.workLocationName ? "border-red-500" : ""}`}
                        placeholder="Enter work location name..."
                      />
                      {errors.workLocationName && <p className="text-red-500 text-sm mt-1">{errors.workLocationName}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label>Code</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.code}
                        readOnly
                        className={`${inputBase} cursor-not-allowed opacity-90`}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label>Branch</Label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={formData.branch}
                        onChange={(e) => handleInputChange("branch", e.target.value)}
                        disabled={branchType !== "HO"}
                        className={`${selectBase} ${branchType !== "HO" ? "opacity-90 cursor-not-allowed" : ""}`}
                      >
                        <option value="">Select</option>
                        {branchList.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4 pt-2">
                      <Label required>Address 1</Label>
                    </div>
                    <div className="col-span-8">
                      <textarea
                        rows={3}
                        value={formData.address1}
                        onChange={(e) => handleInputChange("address1", e.target.value)}
                        className={`${textAreaBase} ${errors.address1 ? "border-red-500" : ""}`}
                        placeholder="Enter address 1..."
                      />
                      {errors.address1 && <p className="text-red-500 text-sm mt-1">{errors.address1}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4 pt-2">
                      <Label>Address 2</Label>
                    </div>
                    <div className="col-span-8">
                      <textarea
                        rows={3}
                        value={formData.address2}
                        onChange={(e) => handleInputChange("address2", e.target.value)}
                        className={textAreaBase}
                        placeholder="Enter address 2..."
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>Country</Label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className={selectBase}
                      >
                        <option value="">Select</option>
                        {countryList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>

                      {isOthersSelected(formData.country) && (
                        <input
                          type="text"
                          value={formData.otherCountryName}
                          onChange={(e) => handleInputChange("otherCountryName", e.target.value)}
                          className={`${inputBase} mt-2`}
                          placeholder="Enter new country name..."
                        />
                      )}

                      {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>State</Label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={formData.state}
                        onChange={(e) => handleStateChange(e.target.value)}
                        className={selectBase}
                        disabled={!formData.country || isOthersSelected(formData.country)}
                      >
                        <option value="">Select</option>
                        {stateList.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>

                      {isOthersSelected(formData.state) && (
                        <input
                          type="text"
                          value={formData.otherStateName}
                          onChange={(e) => handleInputChange("otherStateName", e.target.value)}
                          className={`${inputBase} mt-2`}
                          placeholder="Enter new state name..."
                        />
                      )}

                      {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>District</Label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={formData.district}
                        onChange={(e) => handleDistrictChange(e.target.value)}
                        className={`${selectBase} ${errors.district ? "border-red-500" : ""}`}
                        disabled={!formData.state || String(formData.country) === "-1"}
                      >
                        <option value="">Select</option>
                        {districtList.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>

                      {isOthersSelected(formData.district) && (
                        <input
                          type="text"
                          value={formData.otherDistrictName}
                          onChange={(e) => handleInputChange("otherDistrictName", e.target.value)}
                          className={`${inputBase} mt-2`}
                          placeholder="Enter new district name..."
                        />
                      )}

                      {errors.district && <p className="text-red-500 text-sm mt-1">{errors.district}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>City</Label>
                    </div>
                    <div className="col-span-8">
                      <select
                        value={formData.city}
                        onChange={(e) => handleCityChange(e.target.value)}
                        className={`${selectBase} ${errors.city ? "border-red-500" : ""}`}
                        disabled={!formData.state || String(formData.country) === "-1"}
                      >
                        <option value="">Select</option>
                        {cityList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>

                      {isOthersSelected(formData.city) && (
                        <input
                          type="text"
                          value={formData.otherCityName}
                          onChange={(e) => handleInputChange("otherCityName", e.target.value)}
                          className={`${inputBase} mt-2`}
                          placeholder="Enter new city name..."
                        />
                      )}

                      {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label>Zipcode</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.zipcode}
                        onChange={(e) => handleInputChange("zipcode", e.target.value)}
                        className={inputBase}
                        placeholder="Enter zipcode..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>Contact Person</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange("contactPerson", e.target.value)}
                        className={`${inputBase} ${errors.contactPerson ? "border-red-500" : ""}`}
                        placeholder="Enter contact person..."
                      />
                      {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label>Email</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={inputBase}
                        placeholder="Enter email..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label required>Phone Number</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                        className={`${inputBase} ${errors.phoneNumber ? "border-red-500" : ""}`}
                        placeholder="Enter phone number..."
                      />
                      {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <Label>Mobile</Label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange("mobile", e.target.value)}
                        className={inputBase}
                        placeholder="Enter mobile..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4 pt-2">
                      <Label>Remarks</Label>
                    </div>
                    <div className="col-span-8">
                      <textarea
                        rows={3}
                        value={formData.remarks}
                        onChange={(e) => handleInputChange("remarks", e.target.value)}
                        className={textAreaBase}
                        placeholder="Enter remarks..."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 pt-4">
                  <button
                    onClick={handleSubmit}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaCheck />
                    {isEdit ? "Update" : "Submit"}
                  </button>

                  <button
                    onClick={handleAddNew}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default WorkLocationForm;